'use client';

/* eslint-disable @next/next/no-img-element */
import ProductCard from '@/components/Product/ProductCard';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EProductImageType } from '@/utils/enums/goods';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ListProduct } from '@/api/types/public';
import type { MarketingProductListResponse } from '../../../types';
import {
  getInitialCollaborationProductState,
  loadCollaborationProductPage,
  mergeCollaborationProducts,
} from '../productLoader';
import type { CollaborationProductListData } from '../types';
import { resolveCollaborationAssetUrl } from '../runtimeData';
import styles from '../CollaborationPage.module.css';
import {
  clearCollaborationReturnState,
  getCollaborationReturnStorageKey,
  hasCollaborationReturnStateMarker,
  readCollaborationReturnState,
  saveCollaborationReturnState,
  scheduleCollaborationViewportRestore,
} from '@/utils/collaboration-product-return-state';

const PRODUCT_PAGE_CACHE_VERSION = 1;
const PRODUCT_PAGE_CACHE_TTL_MS = 30 * 60 * 1000;
const COLLABORATION_PRODUCT_HOVER_RULE = [
  EProductImageType.CELEBRITY,
  EProductImageType.MATERIAL,
  EProductImageType.PRODUCT,
];

interface CollaborationProductPageCache {
  version: typeof PRODUCT_PAGE_CACHE_VERSION;
  products: ListProduct[];
  page: number;
  hasMore: boolean;
  savedAt: number;
}

function isValidProductPageCache(value: unknown): value is CollaborationProductPageCache {
  if (!value || typeof value !== 'object') return false;
  const cache = value as Partial<CollaborationProductPageCache>;
  return (
    cache.version === PRODUCT_PAGE_CACHE_VERSION &&
    Array.isArray(cache.products) &&
    typeof cache.page === 'number' &&
    typeof cache.hasMore === 'boolean' &&
    typeof cache.savedAt === 'number' &&
    Date.now() - cache.savedAt <= PRODUCT_PAGE_CACHE_TTL_MS
  );
}

function getProductReturnTargetKey(product: ListProduct, index: number) {
  return String(product.shop_sku || product.id || index);
}

function findProductReturnTarget(section: HTMLElement | null, targetKey: string) {
  return Array.from(
    section?.querySelectorAll<HTMLElement>('[data-collaboration-product-return-target]') || [],
  ).find((element) => element.dataset.collaborationProductReturnTarget === targetKey);
}

function ProductListSkeleton() {
  return (
    <div
      className={styles.productListSkeleton}
      data-collaboration-product-skeleton
      aria-hidden="true"
    >
      <div className={styles.productListSkeletonImage} />
      <div className={styles.productListSkeletonInfo}>
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

export default function CollaborationProductList({
  data,
  initialResponse,
  isWeb,
  returnStateKey,
  onProductClick,
  onProductExposure,
  onProductCollect,
}: {
  data: CollaborationProductListData;
  initialResponse?: MarketingProductListResponse | null;
  isWeb: boolean;
  returnStateKey: string;
  onProductClick?: (product: unknown, index: number, title: string) => void;
  onProductExposure?: (product: unknown, index: number, title: string) => void;
  onProductCollect?: (product: unknown, index: number, collectType: string) => void;
}) {
  const t = useTranslations('activity.campaigns.collaboration');
  const getPageCache = useStore((state) => state.getPageCache);
  const setPageCache = useStore((state) => state.setPageCache);
  const clearPageCache = useStore((state) => state.clearPageCache);
  const returnStorageKey = getCollaborationReturnStorageKey(returnStateKey, 'product-list');
  const pageCacheKey = `collaboration:page-cache:${returnStateKey}:product-list`;
  const restoredPageCache = useMemo(() => {
    if (!hasCollaborationReturnStateMarker(returnStorageKey)) return null;
    const cached = getPageCache(pageCacheKey) as unknown;
    return isValidProductPageCache(cached) ? cached : null;
  }, [getPageCache, pageCacheKey, returnStorageKey]);
  const initialState = getInitialCollaborationProductState({
    initialResponse,
    canLoad: data.productGroupId > 0,
  });
  const [products, setProducts] = useState(
    () => restoredPageCache?.products || initialState.products,
  );
  const [page, setPage] = useState(() => restoredPageCache?.page ?? initialState.page);
  const [hasMore, setHasMore] = useState(() => restoredPageCache?.hasMore ?? initialState.hasMore);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const loadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const restoredRef = useRef(false);
  const restoreCleanupRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const returnState = readCollaborationReturnState(returnStorageKey);
    if (!returnState) return;
    const target = findProductReturnTarget(sectionRef.current, returnState.targetKey);
    if (!target) return;

    if (typeof returnState.viewportTop === 'number') {
      restoreCleanupRef.current = scheduleCollaborationViewportRestore(
        target,
        returnState.viewportTop,
      );
    } else {
      target.scrollIntoView({ block: 'center', inline: 'nearest' });
    }
    clearCollaborationReturnState(returnStorageKey);
    clearPageCache(pageCacheKey);
  }, [clearPageCache, pageCacheKey, returnStorageKey]);

  useEffect(
    () => () => {
      restoreCleanupRef.current();
    },
    [],
  );

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || data.productGroupId <= 0) return;
    loadingRef.current = true;
    setStatus('loading');
    try {
      const nextPage = page + 1;
      const result = await loadCollaborationProductPage({
        groupId: data.productGroupId,
        page: nextPage,
      });
      setProducts((current) => mergeCollaborationProducts(current, result.products));
      setPage(nextPage);
      setHasMore(result.hasMore);
      setStatus('idle');
    } catch (error) {
      logger.error(error, 'Failed to load collaboration products:');
      setStatus('error');
    } finally {
      loadingRef.current = false;
    }
  }, [data.productGroupId, hasMore, page]);

  const handleProductClick = useCallback(
    (product: ListProduct, index: number, currentProduct: ListProduct = product) => {
      const targetKey = getProductReturnTargetKey(product, index);
      const target = findProductReturnTarget(sectionRef.current, targetKey);
      const saved = saveCollaborationReturnState(
        returnStorageKey,
        targetKey,
        target?.getBoundingClientRect().top,
      );
      if (saved) {
        setPageCache(pageCacheKey, {
          version: PRODUCT_PAGE_CACHE_VERSION,
          products,
          page,
          hasMore,
          savedAt: Date.now(),
        } satisfies CollaborationProductPageCache);
      } else {
        clearPageCache(pageCacheKey);
      }
      onProductClick?.(currentProduct, index, data.title);
    },
    [
      clearPageCache,
      data.title,
      hasMore,
      onProductClick,
      page,
      pageCacheKey,
      products,
      returnStorageKey,
      setPageCache,
    ],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMore();
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  if (!data.title && products.length === 0) return null;
  const background = resolveCollaborationAssetUrl(
    isWeb ? data.pcBackgroundImage : data.wapBackgroundImage,
  );

  return (
    <section ref={sectionRef} className={styles.productSection}>
      <div
        className={styles.productStage}
        data-testid="collaboration-product-stage"
        data-overlap={isWeb ? 'pc' : 'wap'}
      >
        <header className={styles.productHeading}>
          {background && <img src={background} alt="" loading="lazy" decoding="async" />}
          <div>
            {data.title && <h2>{data.title}</h2>}
            {data.subTitle && <p>{data.subTitle}</p>}
          </div>
        </header>
        <div
          className={styles.productGrid}
          data-testid="collaboration-product-grid"
          aria-busy={status === 'loading'}
        >
          {products.map((product, index) => (
            <div
              key={product.shop_sku || product.id || index}
              data-collaboration-product-return-target={getProductReturnTargetKey(product, index)}
            >
              <ProductCard
                product={product}
                index={index}
                showTryOn={false}
                showCorrelation
                correlationDisplayType="color-swatch"
                correlationHoverBehavior={isWeb ? 'preview' : undefined}
                correlationIndicatorMotion="static"
                imageHoverRule={COLLABORATION_PRODUCT_HOVER_RULE}
                hoverProductImagePriority="gray-second-only"
                showMember={isWeb}
                showName={false}
                namePriceInline={false}
                showReviewStar
                showTag={isWeb}
                className={`h-full ${
                  isWeb ? 'overflow-hidden rounded-12 bg-auxiliary-light-gray' : 'bg-none'
                }`}
                imageClassName={styles.productListImage}
                productInfoClassName={styles.productListInfo}
                handelClick={(currentIndex, currentProduct) =>
                  handleProductClick(product, currentIndex, currentProduct as ListProduct)
                }
                onValidExposure={(currentIndex, currentProduct) =>
                  onProductExposure?.(currentProduct, currentIndex, data.title)
                }
                wished={(currentProduct, currentIndex) =>
                  onProductCollect?.(currentProduct, currentIndex, '收藏')
                }
                removed={(currentProduct, currentIndex) =>
                  onProductCollect?.(currentProduct, currentIndex, '取消收藏')
                }
              />
            </div>
          ))}
          {status === 'loading'
            ? Array.from({ length: isWeb ? 4 : 2 }, (_, index) => (
                <ProductListSkeleton key={`collaboration-product-skeleton-${index}`} />
              ))
            : null}
        </div>
      </div>
      {hasMore && <div ref={sentinelRef} className={styles.loadSentinel} aria-hidden="true" />}
      {status === 'error' && (
        <button type="button" className={styles.retryButton} onClick={loadMore}>
          {t('retry')}
        </button>
      )}
    </section>
  );
}
